/**
 * Verifica se o backend está realmente acessível
 * Não confia apenas em navigator.onLine
 */

import { getApiBaseUrl } from './getApiUrl.ts';

const HEALTH_CHECK_INTERVAL = 30000; // 30 segundos
const HEALTH_CHECK_TIMEOUT = 5000; // 5 segundos
const OFFLINE_THRESHOLD = 2; // falhas consecutivas necessárias para declarar offline

let backendOnlineStatus = navigator.onLine;
let healthCheckInterval: NodeJS.Timeout | null = null;
let lastHealthCheck = 0;
let consecutiveFailures = 0;

/**
 * Faz um health check no backend
 */
async function checkBackendHealth(): Promise<boolean> {
  // Se não há internet, backend não está online
  if (!navigator.onLine) {
    console.log('[BackendStatus] Navegador offline');
    return false;
  }

  // Sempre obter a URL mais atualizada (pode ter mudado no localStorage)
  // getApiBaseUrl já retorna a raiz do backend (sem /api/v1); health está em /health
  const baseUrl = getApiBaseUrl();
  if (!baseUrl || !baseUrl.trim()) {
    console.error(
      '[BackendStatus] URL base da API vazia. Em produção defina REACT_APP_API_URL_PRODUCTION (build) com o URL do Cloud Run.'
    );
    return false;
  }
  const healthUrl = `${baseUrl.replace(/\/$/, '')}/health`;
  console.log('[BackendStatus] Verificando health em:', healthUrl);
  console.log('[BackendStatus] Hostname atual:', window.location.hostname);
  console.log('[BackendStatus] IP salvo no localStorage:', localStorage.getItem('backend_ip'));

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), HEALTH_CHECK_TIMEOUT);

    const startTime = Date.now();
    const response = await fetch(healthUrl, {
      method: 'GET',
      signal: controller.signal,
      cache: 'no-cache',
      mode: 'cors' // Explicitly set CORS mode
    });

    clearTimeout(timeoutId);
    const duration = Date.now() - startTime;
    lastHealthCheck = Date.now();
    const isOk = response.ok;

    if (isOk) {
      consecutiveFailures = 0;
      const data = await response.text().catch(() => '');
      console.log(`[BackendStatus] Health check OK (${duration}ms):`, data.substring(0, 100));
    } else {
      consecutiveFailures++;
      console.warn(`[BackendStatus] Health check FAILED (${response.status}):`, response.statusText, `(falha ${consecutiveFailures}/${OFFLINE_THRESHOLD})`);
    }

    return isOk || consecutiveFailures < OFFLINE_THRESHOLD;
  } catch (error: any) {
    consecutiveFailures++;
    const errorType = error.name || 'Unknown';
    const errorMessage = error.message || String(error);
    console.error(`[BackendStatus] Backend não está acessível (falha ${consecutiveFailures}/${OFFLINE_THRESHOLD}):`, errorType, errorMessage);
    console.error('[BackendStatus] URL tentada:', healthUrl);
    lastHealthCheck = Date.now();
    return consecutiveFailures < OFFLINE_THRESHOLD;
  }
}

/**
 * Verifica se o backend está online
 * Usa cache se a última verificação foi recente (< 5 segundos)
 */
export async function isBackendOnline(): Promise<boolean> {
  const now = Date.now();
  const timeSinceLastCheck = now - lastHealthCheck;

  // Se verificamos recentemente (< 5s), retorna o status em cache
  if (timeSinceLastCheck < 5000) {
    return backendOnlineStatus;
  }

  // Faz nova verificação
  backendOnlineStatus = await checkBackendHealth();
  return backendOnlineStatus;
}

/**
 * Verifica se o backend está online (síncrono - retorna último status conhecido)
 */
export function isBackendOnlineSync(): boolean {
  return backendOnlineStatus;
}

/**
 * Inicia monitoramento periódico do backend
 */
export function startBackendMonitoring(callback?: (isOnline: boolean) => void) {
  // Verificação inicial
  checkBackendHealth().then((isOnline) => {
    if (backendOnlineStatus !== isOnline) {
      backendOnlineStatus = isOnline;
      callback?.(isOnline);
    }
  });

  // Limpar intervalo anterior se existir
  if (healthCheckInterval) {
    clearInterval(healthCheckInterval);
  }

  // Verificar periodicamente
  healthCheckInterval = setInterval(async () => {
    const isOnline = await checkBackendHealth();
    if (backendOnlineStatus !== isOnline) {
      const previousStatus = backendOnlineStatus;
      backendOnlineStatus = isOnline;
      console.log(`[BackendStatus] Status mudou: ${previousStatus} -> ${isOnline}`);
      callback?.(isOnline);
    }
  }, HEALTH_CHECK_INTERVAL);

  // Também verificar quando voltar online
  window.addEventListener('online', async () => {
    const isOnline = await checkBackendHealth();
    if (backendOnlineStatus !== isOnline) {
      backendOnlineStatus = isOnline;
      callback?.(isOnline);
    }
  });
}

/**
 * Para o monitoramento
 */
export function stopBackendMonitoring() {
  if (healthCheckInterval) {
    clearInterval(healthCheckInterval);
    healthCheckInterval = null;
  }
}

/**
 * Força uma nova verificação do backend (útil quando IP é atualizado)
 */
export async function forceBackendCheck(callback?: (isOnline: boolean) => void): Promise<boolean> {
  console.log('[BackendStatus] Forçando verificação do backend...');
  lastHealthCheck = 0; // Reset cache para forçar nova verificação
  consecutiveFailures = 0; // Reset contador para não herdar falhas anteriores
  const isOnline = await checkBackendHealth();
  backendOnlineStatus = isOnline;
  callback?.(isOnline);
  return isOnline;
}


